import { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { X, Plus, Trash2, ExternalLink, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentLinksModal = ({ customer, onClose, getAuth }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLink, setNewLink] = useState({
    amount: "",
    description: "",
    payment_url: ""
  });

  const fetchLinks = async () => {
    if (!customer) return;
    const headers = getAuth();
    if (!headers) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/customers/${customer.id}/payment-links`,
        { headers }
      );
      setLinks(response.data || []);
    } catch (error) {
      console.error("Error fetching payment links:", error);
      toast.error("Erreur lors du chargement des liens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [customer]);

  const handleAddLink = async (e) => {
    e.preventDefault();
    const headers = getAuth();
    if (!headers) return;

    if (!newLink.amount || !newLink.description || !newLink.payment_url) {
      toast.error("Tous les champs sont obligatoires");
      return;
    }

    // Validate URL
    try {
      new URL(newLink.payment_url);
    } catch {
      toast.error("L'URL de paiement n'est pas valide");
      return;
    }

    try {
      await axios.post(
        `${API}/admin/customers/${customer.id}/payment-links`,
        {
          customer_id: customer.id,
          amount: parseFloat(newLink.amount),
          description: newLink.description,
          payment_url: newLink.payment_url
        },
        { headers }
      );
      toast.success("Lien de paiement ajouté");
      setNewLink({ amount: "", description: "", payment_url: "" });
      setShowAddForm(false);
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm("Supprimer ce lien de paiement ?")) return;
    
    const headers = getAuth();
    if (!headers) return;

    try {
      await axios.delete(`${API}/admin/payment-links/${linkId}`, { headers });
      toast.success("Lien supprimé");
      fetchLinks();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  if (!customer) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Liens de paiement
            </h3>
            <p className="text-sm text-slate-500">
              {customer.first_name} {customer.last_name}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {/* Add Button */}
          {!showAddForm && (
            <Button
              onClick={() => setShowAddForm(true)}
              className="w-full mb-4 bg-primary hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un lien de paiement
            </Button>
          )}

          {/* Add Form */}
          {showAddForm && (
            <form onSubmit={handleAddLink} className="mb-4 p-4 bg-slate-50 rounded-lg border">
              <div className="space-y-3">
                <div>
                  <Label>Montant (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="Ex: 45.00"
                    value={newLink.amount}
                    onChange={(e) => setNewLink({...newLink, amount: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    placeholder="Ex: Facture pressing décembre"
                    value={newLink.description}
                    onChange={(e) => setNewLink({...newLink, description: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>URL de paiement</Label>
                  <Input
                    type="url"
                    placeholder="https://..."
                    value={newLink.payment_url}
                    onChange={(e) => setNewLink({...newLink, payment_url: e.target.value})}
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Colle ici le lien Stripe, PayPal ou autre
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowAddForm(false);
                    setNewLink({ amount: "", description: "", payment_url: "" });
                  }}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-primary">
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
            </form>
          )}

          {/* Links List */}
          {loading ? (
            <div className="text-center py-8 text-slate-500">Chargement...</div>
          ) : links.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Aucun lien de paiement</p>
              <p className="text-sm">Les liens sont visibles pendant 30 jours</p>
            </div>
          ) : (
            <div className="space-y-3">
              {links.map((link) => (
                <div
                  key={link.id}
                  className="p-3 rounded-lg border bg-white border-slate-200"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-lg">{link.amount.toFixed(2)}€</span>
                      </div>
                      <p className="text-sm text-slate-700 truncate">{link.description}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Ajouté le {format(new Date(link.created_at), "d MMM yyyy", { locale: fr })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(link.payment_url, "_blank")}
                        title="Ouvrir le lien"
                        className="h-8 w-8"
                      >
                        <ExternalLink className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(link.id)}
                        title="Supprimer"
                        className="h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t shrink-0 bg-slate-50">
          <p className="text-xs text-center text-slate-500">
            Les liens sont visibles par le client pendant 6 mois après leur création
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentLinksModal;
